import { format } from "date-fns";
import { inject } from "mobx-react";
import { SyntheticEvent, useCallback, useEffect } from "react";
import * as React from "react";

import { VolumeInCircleIcon } from "../../../../../core/components/icons/VolumeInCircleIcon.component";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { isPWSSessionBackend, PWSSessionState } from "../../../../../core/lib/auth/backends/pws";
import { useNewWindow } from "../../../../../core/lib/window";
import { coastMediaService } from "../../../../../core/services/PWS";
import { Button, ButtonKind } from "../../../../../core/ui";
import type { Store } from "../../../../../../client/stores";

interface CoastShowAudioBoxPlayerLinkProps {
    showDate: Date | number;
    store?: Store;
    children: React.ReactNode;
}

interface CoastShowAudioBoxPlayerLinkLoaderProps {
    showSlugs: string[];
    showDate: Date | number;
    showLoading?: boolean;
    store?: Store;
    children?: React.ReactNode;
}

export const CoastShowAudioBoxPlayerLink = inject("store")(
    ({ showDate, store, children }: CoastShowAudioBoxPlayerLinkProps) => {
        const session = store?.session.currentSession;
        const [openWindow, closeWindow] = useNewWindow();

        const handleClick = useCallback(
            (e: SyntheticEvent) => {
                e.preventDefault();
                if (!isPWSSessionBackend(session)) {
                    return null;
                }

                session.executeOnValidSubscription(() => {
                    openWindow({
                        url: `/video-playlist/${format(showDate, "yyyy/MM/d")}/`,
                        target: "coast-player",
                        features: {
                            width: 769,
                            height: 800,
                        },
                    });
                });
            },
            [session, openWindow, showDate],
        );

        const handleStatusChange = useCallback(
            (state: PWSSessionState) => {
                if (!state.authenticated || !state.hasActiveSubscription) {
                    closeWindow("coast-player");
                }
            },
            [closeWindow],
        );

        useEffect(() => {
            if (isPWSSessionBackend(session)) {
                session?.onStatusChanged.subscribe(handleStatusChange);
                return () => session?.onStatusChanged.unsubscribe(handleStatusChange);
            }
        }, [session, handleStatusChange]);

        if (!store) {
            return null;
        }

        return (
            <section className="audiobox-section coast-player-link">
                <div className="audiobox-section-value">
                    <div className="link-with-icon">
                        <Button kind={ButtonKind.CUSTOMIZED} className="media-link volume" click={handleClick}>
                            <VolumeInCircleIcon fill="#fff" />
                        </Button>
                        <Button kind={ButtonKind.CUSTOMIZED} className="media-link listen" click={handleClick}>
                            Listen with Coast Player
                        </Button>
                    </div>
                    <div className="link-with-icon">{children}</div>
                </div>
            </section>
        );
    },
);

export const CoastShowAudioBoxPlayerLinkLoader = inject("store")(
    ({ showSlugs, showDate, store, children, showLoading = true }: CoastShowAudioBoxPlayerLinkLoaderProps) => {
        const session = store?.session.currentSession;

        if (!store || !isPWSSessionBackend(session)) {
            return null;
        }

        return (
            <Remote
                loader={() => coastMediaService.getMediaEpisodesBySlugs(showSlugs, session?.state?.accessToken ?? "")}
                cacheKey={`coast-episodes-playlist-${showDate}-${session.state.hasActiveSubscription}`}
                showLoading={showLoading}
            >
                {/* the episodes themselves don't matter, just the count
                so no need to re-render on login/logout */}
                {({ data }) =>
                    data?.videos?.length ? (
                        <CoastShowAudioBoxPlayerLink showDate={showDate}>{children}</CoastShowAudioBoxPlayerLink>
                    ) : (
                        <>{children}</>
                    )
                }
            </Remote>
        );
    },
);
